import { differenceInCalendarDays } from 'date-fns'
import { object } from 'prop-types'

const WEEKEND_CATEGORY = 'weekend'
const POST_WEEKEND_CATEGORY = 'postweekend'

export default function modifyScheduleData(
  planningResponse,
  defaultCoursePlan,
  startDate: string | Date = '2025-03-25T18:30:00'
) {
  if (Object.keys(planningResponse).length === 0) {
    return null
  }
  const sessionScheduleOrder = ['locked', 'standard', 'weekend', 'postweekend']
  const scheduleSessions = {
    training: [],
    course: [],
    totalWeekendSessions: 0
  }
  let episodeCounter = 1
  let trainingCounter = 1
  let weekendCounter = 1

  scheduleSessions.totalWeekendSessions = (
    defaultCoursePlan.weekend || []
  ).length

  const trainingEpisodeNumberMap = defaultCoursePlan?.training.reduce(
    (accumulator, { id }, index) => {
      accumulator[id] = index + 1 // Assuming episode numbers start from 1
      return accumulator
    },
    {}
  )

  //course episode numbers
  const courseEpisodeNumberMap = sessionScheduleOrder.reduce(
    (acc, category) => {
      ;(defaultCoursePlan[category] || []).forEach(({ id }) => {
        acc[id] = episodeCounter++
      })
      return acc
    },
    {}
  )

  const weekendSessionNumberMap = defaultCoursePlan.weekend.reduce(
    (accumulator, { id }, index) => {
      accumulator[id] = index + 1
      return accumulator
    },
    {}
  )
  // Combine all items in different buckets into a single array
  const alphaSessions = Object.entries(planningResponse).flatMap(
    ([key, items]) => {
      return (items as Array<any>).map((item) => ({
        date: item.date,
        id: item.id,
        episodeName: item.title,
        ...(item.skipped === true ? { skipped: true } : {})
      }))
    }
  )

  const updatedItems = findSessionStatus(alphaSessions)

  const sessionStatusMap = updatedItems.reduce((acc, { id, status }) => {
    acc[id] = status // Apply setStatus to each item's date
    return acc
  }, {})

  const courseSessions = sessionScheduleOrder.reduce((acc, category) => {
    ;(planningResponse[category] || []).forEach(
      ({ id, title, thumbnail, date, type, skipped }) => {
        acc.push({
          id,
          title,
          thumbnail,
          date,
          type,
          episodeNumber: courseEpisodeNumberMap[id],
          weekendEpisodeNumber: weekendSessionNumberMap[id] ?? '',
          skipped: skipped ?? false,
          status: sessionStatusMap[id],
          numDaysFromStart: numberOfDaysSinceStart(date, startDate),
          category: category
        })
      }
    )
    return acc
  }, [])

  const trainingSessions = planningResponse.training.map(
    ({ id, title, thumbnail, date, type, skipped }) => ({
      id,
      title,
      thumbnail,
      date,
      type,
      trainingEpisodeNumber: trainingEpisodeNumberMap[id],
      skipped: skipped ?? false, //!!planningResponse?.available?.find((item) => item.id === id),
      numDaysFromStart: numberOfDaysSinceStart(date, startDate),
      status: sessionStatusMap[id],
      category: 'training'
    })
  )

  scheduleSessions.training = trainingSessions

  scheduleSessions.course = courseSessions

  return scheduleSessions
}

export function setStatus(
  sessionDate: string | Date,
  hasCurrentSession: boolean = false
): { status: 'completed' | 'current' | 'upcoming'; updatedFlag: boolean } {
  if (numberOfDaysSinceStart(sessionDate, new Date()) < 0) {
    return { status: 'completed', updatedFlag: hasCurrentSession }
  } else if (
    numberOfDaysSinceStart(sessionDate, new Date()) >= 0 &&
    !hasCurrentSession
  ) {
    return { status: 'current', updatedFlag: true }
  }
  return { status: 'upcoming', updatedFlag: hasCurrentSession }
}

export function numberOfDaysSinceStart(sessionDate, startDate) {
  return differenceInCalendarDays(
    sessionDate instanceof Date ? sessionDate : new Date(sessionDate),
    startDate instanceof Date ? startDate : new Date(startDate)
  )
}

export function findSessionStatus(alphaSessions: any[]) {
  // Assign statuses (completed, current, upcoming)
  let hasCurrent = false
  const sortedSessions = [...alphaSessions].sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    return dateA - dateB
  })

  const updatedItems = sortedSessions.map((item) => {
    const sessionDate = new Date(item.date)
    const sessionSkipped = item.skipped
    const { status, updatedFlag } = setStatus(
      sessionDate,
      hasCurrent || sessionSkipped
    )
    if (!sessionSkipped) {
      hasCurrent = updatedFlag
    }

    return {
      ...item,
      status // Attach the calculated status
    }
  })
  return updatedItems
}
